import { useNavigate, useParams } from 'react-router-dom';
import {
  useEffect, useState, useMemo, useRef,
} from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Button,
  Group,
  Select,
  Text,
} from '@mantine/core';
import {
  isFunction,
  isNumber,
  isObject,
} from '@/helpers/is';
import { DIRECTIONS, MONTHS } from '@/helpers/enums';
import { objectToQueryString, queryStringToObject } from '@/helpers/string';
import { withErrorBoundary } from 'react-error-boundary';
import { createRangeFromArrays, createYearsArray } from '@/helpers/dates';
import routes from '@/routes';
import { DataTable } from 'mantine-datatable';
import { routeWithParams } from '@/helpers/route';
import prepareTranslate from '@/helpers/dictionary';
import { isValid, parse } from 'date-fns';
import styles from './style.module.css';
import Export from './Export';
import Columns from './Columns';
import ColumnContent from './ColumnContent';
import {
  calculateTotalPages,
  getCleanQuery,
  getDirtyQuery,
  getRangeDataByNumber,
  getRowsData,
  getRowsIndexes,
  validateData,
  removeEmptyFilters,
} from './Table.functions';
import ErrorFallback from '../ErrorFallback';
import NoData from '../Chart/noData';
import ColumnFilter from './ColumnFilter';
import SearchInput from '../SearchInput';

// TODO: add sort ability

const TableComponent = (props) => {
  const {
    columns = [],
    onColumns = () => { },
    isExportActive = true,
    isColumnsActive = true,
    getData = () => {}, // function signature (start: {number}, end: {number}, query: {object})
    rowsPerPage = 100,
    selectionActions = [],
    childElement: ChildElement,
    childGetData = () => { },
    exportOptions = null,
    childProps,
    useDateSelectors = false,
    defaultFilters,
    emailtemplateId,
    createFile = () => { },
    noDataAction,
    showSelectionColumn,
    serverSorting = true,
    serverPagination = false,
    columnFilters,
    setColumnFilters,
    filterOptions,
    isUrlChange = false,
    withPagination = true,
    onSelectRow = () => { },
    showSelectionColumnHeader = true,
    withSearchInput = false,
    searchInputProps = {},
    showEmptyState = true,
    height = '80vh',
    dynamicRowHeight = false,
  } = props;

  const { query: rawUrlQuery } = useParams();
  const [values, setValues] = useState([]); // full rows data
  const [rows, setRows] = useState([]); // table rows on page
  const [loading, setLoading] = useState(false); // loading
  const [page, setPage] = useState(1); // page number
  const [query, setQuery] = useState({}); // filter query
  const [formattedQueryForChild, setFormattedQueryForChild] = useState({}); // formated for child
  const [totalRows, setTotalRows] = useState(0); // total number of rows
  const [totalPages, setTotalPages] = useState(0); // total page count
  const [selection, setSelection] = useState(showSelectionColumn ? [] : null); // total page count
  const [isSorting, setIsSorting] = useState(false);
  const sortResetRef = useRef(false);
  const isFirstFilterEffectRef = useRef(true);

  const { id: reportId } = useParams();
  const navigate = useNavigate();
  const t = prepareTranslate();

  const [sortStatus, setSortStatus] = useState({
    columnAccessor: null,
    direction: null,
  });

  const actions = selectionActions.map((action) => {
    const {
      title = null,
      icon = null,
      onClick,
    } = action;

    const handleActionClick = async () => {
      if (isFunction(onClick)) {
        await onClick(selection);
      }
    };

    return <Button
      key={title}
      onClick={handleActionClick}
      size='xs'
      variant='outline'
      leftSection={icon}
    >
      {title}
    </Button>;
  });

  /**
   * go over "values" state and check there are data in start and end indexes.
   * call server with missing data, and fill "values" state
   * @param {number} start
   * @param {number} end
   */
  const fillDataFromServer = async (start, end, value, searchObj) => {
    if (!isNumber(start) || !isNumber(end)) {
      // eslint-disable-next-line no-console
      console.error('One of the variables (start or end) is not a number. cant fill data from server');
    }

    if (serverPagination) {
      setLoading(true);
      let formattedQuery;
      if (rawUrlQuery) {
        formattedQuery = queryStringToObject(rawUrlQuery);
        const {
          month, year, agent, company, product,
        } = formattedQuery;
        const [firstDate, lastDate] = createRangeFromArrays(year, month);
        const filterQuery = {
          company, product, identifier: agent, date: [firstDate, lastDate],
        };
        setFormattedQueryForChild((prev) => {
          if (JSON.stringify(prev) === JSON.stringify(formattedQuery)) return prev;
          return formattedQuery;
        });
        setQuery(getDirtyQuery(filterQuery));
      } else {
        const cleanFilters = columnFilters ? removeEmptyFilters(columnFilters) : query;
        const childQuery = { ...query, ...cleanFilters };
        formattedQuery = getCleanQuery(childQuery);
        setFormattedQueryForChild((prev) => {
          if (JSON.stringify(prev) === JSON.stringify(childQuery)) return prev;
          return childQuery;
        });
      }
      if (searchObj) {
        formattedQuery = { ...formattedQuery, ...searchObj };
      }
      try {
        const newRows = await getData(start, end, formattedQuery, sortStatus);
        if (!newRows) return;
        const { data, count } = newRows;
        if (!validateData(data, count)) return;
        setRows([...data]);
        setTotalRows(count);
        const newTotalPages = calculateTotalPages(count, rowsPerPage);
        if (totalPages !== newTotalPages) setTotalPages(newTotalPages);
      } finally {
        setLoading(false);
      }
      return;
    }

    let index = start - 1;
    const newValues = value; // start with actual data (values)
    const startExists = isObject(newValues[start - 1]);
    const endExists = isObject(newValues[end - 1]);
    let formattedQuery;
    let filterQuery;

    // check if we need to bring new values
    if (!startExists || (end <= totalPages && !endExists) || (serverSorting && isSorting)) {
      setLoading(true);
      try {
        const startRange = getRangeDataByNumber(start);
        const endRange = getRangeDataByNumber(end);
        const startIndex = Math.min(...startRange, ...endRange);
        const endIndex = Math.max(...startRange, ...endRange);

        if (rawUrlQuery) {
          formattedQuery = queryStringToObject(rawUrlQuery);
          const {
            month,
            year,
            agent,
            company,
            product,
          } = formattedQuery;

          const [firstDate, lastDate] = createRangeFromArrays(year, month);

          filterQuery = {
            company,
            product,
            identifier: agent,
            date: [firstDate, lastDate],
          };

          setFormattedQueryForChild(formattedQuery);
          setQuery(getDirtyQuery(filterQuery));
        } else {
          const cleanFilters = columnFilters ? removeEmptyFilters(columnFilters) : query;
          formattedQuery = getCleanQuery({ ...query, ...cleanFilters });
          setFormattedQueryForChild({ ...query, ...cleanFilters });
        }
        if (searchObj) {
          formattedQuery = { ...formattedQuery, ...searchObj };
        }

        const newRows = await getData(startIndex, endIndex, formattedQuery, sortStatus);

        if (!newRows) {
          return;
        }

        if (!newValues) {
          // eslint-disable-next-line no-console
          console.error('getData not return an object {data: array, count: number}');
          return;
        }

        const {
          data,
          count,
        } = newRows;

        if (!validateData(data, count)) {
          // eslint-disable-next-line no-console
          console.error('getData is not valid');
          return;
        }

        data.forEach((element) => {
          newValues[index] = element;
          index += 1;
        });

        setValues(newValues);
        setTotalRows(count);

        const newTotalPages = calculateTotalPages(count, rowsPerPage);

        if (totalPages !== newTotalPages) {
          setTotalPages(newTotalPages);
        }
      } finally {
        setLoading(false);
      }
    }
  };

  /**
   * update page rows that will be visible on table.
   * this is the major function that starts everything
   */
  const updateRows = async (value, searchObj) => {
    const rowsPerpage = withPagination ? rowsPerPage : (totalRows || 0);
    const [startIndex, endIndex] = getRowsIndexes(page, rowsPerpage);
    await fillDataFromServer(startIndex, endIndex, value, searchObj);
    if (!serverPagination) {
      const newRows = getRowsData(startIndex, endIndex, value);
      setRows([...newRows]);
    }
  };

  const onExportSubmit = async (d) => {
    let formattedQuery;

    if (rawUrlQuery) {
      formattedQuery = queryStringToObject(rawUrlQuery);
    } else {
      formattedQuery = getCleanQuery(query);
    }

    const { fileData, fileTitle } = await createFile(d, formattedQuery);
    return { fileData, fileTitle };
  };

  const handlePaginationChange = (pageNumber) => {
    setPage(pageNumber);
  };

  useEffect(() => {
  }, [formattedQueryForChild, rows]);

  const handleSortChange = (status) => {
    const { direction, columnAccessor } = status;
    let modifiedDirection = direction;
    if (sortStatus.columnAccessor !== columnAccessor) {
      modifiedDirection = 'asc';
    }
    const updatedSort = {
      columnAccessor,
      direction: modifiedDirection,
    };
    setSortStatus(updatedSort);
  };

  const getKeyValue = (source, path) => {
    const keys = path.split('.');
    let currentValue = source;
    while (keys.length) {
      const currentKey = keys.shift();
      if (Object.prototype.hasOwnProperty.call(currentValue, currentKey)) {
        currentValue = currentValue[currentKey];
      } else {
        currentValue = undefined;
        break;
      }
    }
    return currentValue === undefined ? '' : currentValue;
  };

  const compareBoolean = (a, b) => {
    if (a === b) return 0;
    return a ? 1 : -1;
  };

  function convertDateToDateObj(format, dateStr) {
    const parsedDate = parse(dateStr, format, new Date());
    if (!isValid(parsedDate)) {
      return null;
    }
    return parsedDate;
  }

  const compareDates = (format, first, second) => {
    const formattedFirst = convertDateToDateObj(format, first);
    const formattedSecond = convertDateToDateObj(format, second);
    return formattedFirst - formattedSecond;
  };

  const doSort = (sortObj = {}, items = []) => {
    const { columnAccessor, direction } = sortObj;
    const output = items.sort((a, b) => {
      const first = (direction === DIRECTIONS.asc) ? a : b;
      const second = (direction === DIRECTIONS.asc) ? b : a;
      const firstValue = getKeyValue(first, columnAccessor);
      const secondValue = getKeyValue(second, columnAccessor);
      const column = columns.find((col) => col.accessor === columnAccessor);

      switch (column.sort?.type) {
        case 'string':
          return firstValue.localeCompare(secondValue);
        case 'number':
          return parseFloat(firstValue) - parseFloat(secondValue);
        case 'boolean':
          return compareBoolean(firstValue, secondValue);
        case 'date':
          return compareDates(column.sort.format, firstValue, secondValue);
        default:
          return firstValue.localeCompare(secondValue);
      }
    });

    return output;
  };

  const handleDateChange = (key, value) => {
    const parsedQuery = queryStringToObject(rawUrlQuery);
    parsedQuery[key] = [value];
    const { path } = routes.reportsView;
    const queryParams = objectToQueryString({
      month: parsedQuery.month,
      year: parsedQuery.year,
      company: parsedQuery.company,
      product: parsedQuery.product,
      category: parsedQuery.category,
      showChart: parsedQuery.showChart,
      useDateSelectors: parsedQuery.useDateSelectors,
      agent: parsedQuery.agent,
    });
    const fullPath = routeWithParams(path, { id: reportId, query: queryParams });
    navigate(fullPath);
  };

  useEffect(() => {
    if (!sortStatus?.columnAccessor) return;
    setPage(1);
    if (serverPagination || serverSorting) {
      setIsSorting(true);
    } else {
      const sorted = doSort(sortStatus, values);
      const [startIndex, endindex] = getRowsIndexes(page, rowsPerPage);
      const pageRow = getRowsData(startIndex, endindex, sorted);
      setRows([...pageRow]);
      setValues(sorted);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortStatus]);

  useEffect(() => {
    if (sortResetRef.current) {
      sortResetRef.current = false;
      return;
    }
    const fetchRows = async () => {
      await updateRows(values);
      if (isSorting) {
        sortResetRef.current = true;
        setIsSorting(false);
      }
    };
    fetchRows();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, isSorting]);

  useEffect(() => {
    if (isFirstFilterEffectRef.current) {
      isFirstFilterEffectRef.current = false;
      return;
    }
    const fetchRows = async () => {
      if (!isUrlChange) {
        setPage(1);
        setValues([]);
        await updateRows([]);
      }
    };
    fetchRows();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columnFilters]);

  const modifiedColumns = useMemo(
    () => columns.map((col) => {
      const {
        accessor, actions: coloumnActions, transforms, type, title, filterType, menuLabel,
      } = col;
      const base = {
        ...col,
        render: (row) => (
          <ColumnContent
            accessor = {accessor}
            item={row}
            actions={coloumnActions}
            transforms={transforms}
            type={type}
            menuLabel={menuLabel}
          />
        ),
      };

      if (col.isFilterActive) {
        const value = columnFilters[accessor];
        base.filter = (
          <ColumnFilter
            key={accessor}
            label={title}
            accessor={accessor}
            filters={columnFilters}
            setFilters={setColumnFilters}
            options={filterOptions[accessor] ?? []}
            type={filterType}
            isUrlChange={isUrlChange}
            rawUrlQuery={rawUrlQuery}
            reportId={reportId}
          />
        );
        base.filtering = (() => {
          if (typeof value === 'string') {
            return value.trim() !== '';
          }
          if (Array.isArray(value)) {
            return value.length > 0;
          }
          return false;
        })();
      }

      return base;
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [columns, columnFilters, filterOptions],
  );

  const handdleSelectRow = (row) => {
    setSelection(row);
    onSelectRow(row);
  };

  const tableHeight = useMemo(() => {
    if (withPagination && !dynamicRowHeight) {
      return height;
    }
    return rows.length > rowsPerPage ? height : 'fit-content';
  }, [withPagination, rows, height, rowsPerPage, dynamicRowHeight]);

  const handleSearchClick = (searchValue) => {
    const searchElements = {};
    if (searchValue && searchValue.trim() !== '') {
      Object.entries(searchInputProps.searchKey).forEach(([outerKey, innerKeys]) => {
        const innerQuery = {};

        innerKeys.forEach((innerKey) => {
          innerQuery[innerKey] = searchValue;
        });

        searchElements[outerKey] = innerQuery;
      });
    }
    const searchObj = { searchElements };
    setRows([]);
    setPage(1);
    updateRows([], searchObj);
  };

  return (
    <div className={styles.wrapper}>
      <div className={styles.actions}>
        <Group>
          {useDateSelectors && (
            <>
              <div className={styles.selectDateContainer}>
                <Select
                  w='7rem'
                  data={MONTHS}
                  value={defaultFilters.month}
                  onChange={(value) => handleDateChange('month', value)}
                />
                <Select
                  w='7rem'
                  autoComplete='off'
                  data={createYearsArray()}
                  value={defaultFilters.year}
                  onChange={(value) => handleDateChange('year', value)}
                />
              </div>
            </>)}

          {isColumnsActive && <Columns columns={columns} onColumns={onColumns} />}

        </Group>

        {(selection?.length > 0 && showSelectionColumnHeader) && (
          <Group mt="md">
            {actions}
            <Text size='sm'>
              {t('components.table.selected', [selection.length])}
            </Text>
          </Group>
        )}
      </div>

      {ChildElement && <div className={styles.childElement}>
        {/* eslint-disable-next-line max-len */}
        <ChildElement getData={childGetData} getDataQuery={formattedQueryForChild} dropFilters={true} {...childProps} />
      </div>
      }

      {
        (isExportActive && rows && rows.length > 0) && (
          <Export
            onExportSubmit={onExportSubmit}
            options={exportOptions}
            columns={columns}
            tableLoading={loading}
            rowsData={rows}
            emailtemplateId={emailtemplateId}
          />
        )
      }

      {withSearchInput && (
        <Box w={'20rem'} mt='md'>
          <SearchInput {...searchInputProps} onSearch={handleSearchClick} />
        </Box>
      )}

      {!showEmptyState && rows.length === 0 && !loading ? null : <div className={styles.table}>
         <DataTable
          records={rows}
          totalRecords={withPagination ? totalRows : undefined}
          recordsPerPage={withPagination ? rowsPerPage : undefined}
          page={withPagination ? page : undefined}
          onPageChange={withPagination ? handlePaginationChange : undefined}
          columns={modifiedColumns}
          sortStatus={sortStatus}
          onSortStatusChange={handleSortChange}
          selectedRecords={selection}
          onSelectedRecordsChange={handdleSelectRow}
          fetching={loading}
          emptyState={
            <NoData
              noDataAction={noDataAction}
              title={t('components.rows.empty.title')}
            />
          }
          noRecordsText=""
          classNames={{
            root: `${styles.tableRoot} ${rows.length === 0 ? styles.noData : ''}`,
            table: styles.tableElement,
            pagination: styles.pagination,
            paginationText: styles.paginationText,
            paginationPages: styles.paginationPages,
          }}
          rowClassName={({ index, totalRecords }) => (index === totalRecords - 1 ? 'last-row' : '')
          }
          paginationText={withPagination ? ({ from, to, totalRecords }) => `מציג פרטים ${from} עד ${to} מתוך ${totalRecords}` : undefined}
          height={tableHeight}
        />
      </div>

      }
    </div>
  );
};

TableComponent.propTypes = {
  columns: PropTypes.array,
  withPagination: PropTypes.bool,
  onColumns: PropTypes.func,
  isExportActive: PropTypes.bool,
  isColumnsActive: PropTypes.bool,
  getData: PropTypes.func,
  rowsPerPage: PropTypes.number,
  totalCount: PropTypes.number,
  selectionActions: PropTypes.array,
  childElement: PropTypes.object,
  childGetData: PropTypes.func,
  exportOptions: PropTypes.array,
  childProps: PropTypes.object,
  noDataAction: PropTypes.string,
  useDateSelectors: PropTypes.bool,
  defaultFilters: PropTypes.object,
  emailtemplateId: PropTypes.string,
  createFile: PropTypes.func,
  showSelectionColumn: PropTypes.bool,
  serverSorting: PropTypes.bool,
  serverPagination: PropTypes.bool,
  columnFilters: PropTypes.object,
  setColumnFilters: PropTypes.func,
  filterOptions: PropTypes.object,
  isUrlChange: PropTypes.bool,
  onSelectRow: PropTypes.func,
  showSelectionColumnHeader: PropTypes.bool,
  withSearchInput: PropTypes.bool,
  searchInputProps: PropTypes.object,
  height: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
  ]),
  showEmptyState: PropTypes.bool,
  dynamicRowHeight: PropTypes.bool,
};

export default withErrorBoundary(TableComponent, {
  FallbackComponent: ErrorFallback,
});
